import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Download, FileText, Loader2, MessageSquare, Paperclip, Pencil, Send, UserPlus, Upload,
} from "lucide-react";
import api, { apiError, downloadFile } from "../lib/api";
import {
  STATUSES, fmtDateTime, fmtSize, useLabels,
} from "../lib/constants";
import { useLanguage } from "../i18n/LanguageContext";
import { CompanyBadge, PriorityBadge, StatusBadge, TypeBadge } from "../components/Badges";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const STAFF = ["lawyer", "admin", "super_admin"];

export default function CaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const L = useLabels();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [caseData, setCaseData] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [notFound, setNotFound] = useState(false);

  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusComment, setStatusComment] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);

  const [comment, setComment] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  const isStaff = STAFF.includes(user.role);

  const load = useCallback(async () => {
    try {
      const [c, cm, at] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get(`/cases/${id}/comments`),
        api.get(`/cases/${id}/attachments`),
      ]);
      setCaseData(c.data);
      setComments(cm.data);
      setAttachments(at.data);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isStaff) api.get("/users/lawyers").then((r) => setLawyers(r.data)).catch(() => {});
  }, [isStaff]);

  if (notFound) {
    return (
      <div className="py-20 text-center" data-testid="case-not-found">
        <p className="text-sm text-slate-500">{t("detail.notFound")}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/cases")}>{t("detail.back")}</Button>
      </div>
    );
  }
  if (!caseData) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="case-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-delta-navy border-t-transparent" />
      </div>
    );
  }

  const submitStatus = async () => {
    if (!newStatus || statusComment.trim().length < 2) {
      toast.error(t("detail.needStatus"));
      return;
    }
    setStatusBusy(true);
    try {
      await api.patch(`/cases/${id}/status`, { status: newStatus, comment: statusComment.trim() });
      toast.success(t("detail.statusUpdated"));
      setStatusOpen(false);
      setNewStatus("");
      setStatusComment("");
      load();
    } catch (e) {
      toast.error(apiError(e, t("detail.statusFail")));
    } finally {
      setStatusBusy(false);
    }
  };

  const assignLawyer = async (lawyerId) => {
    try {
      await api.patch(`/cases/${id}`, { assigned_lawyer_id: lawyerId });
      toast.success(t("detail.assignOk"));
      load();
    } catch (e) {
      toast.error(apiError(e, t("detail.assignFail")));
    }
  };

  const openEdit = () => {
    setEditForm({
      title: caseData.title,
      description: caseData.description,
      priority: caseData.priority,
      company: caseData.company,
      case_type: caseData.case_type,
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    setEditBusy(true);
    try {
      await api.patch(`/cases/${id}`, editForm);
      toast.success(t("detail.updated"));
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, t("detail.updateFail")));
    } finally {
      setEditBusy(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setCommentBusy(true);
    try {
      await api.post(`/cases/${id}/comments`, { message: comment.trim() });
      setComment("");
      load();
    } catch (e2) {
      toast.error(apiError(e2, t("detail.commentFail")));
    } finally {
      setCommentBusy(false);
    }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/cases/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("detail.uploadedToast", { name: f.name }));
      load();
    } catch (e2) {
      toast.error(apiError(e2, t("detail.uploadFail")));
    } finally {
      setUploadBusy(false);
    }
  };

  const downloadAttachment = async (a) => {
    try {
      await downloadFile(`/files/${a.id}/download`, a.original_filename);
    } catch {
      toast.error(t("detail.downloadFail"));
    }
  };

  const meta = [
    [t("detail.metaType"), L.type[caseData.case_type]],
    [t("detail.metaCompany"), L.company[caseData.company]],
    [t("detail.metaPriority"), L.priority[caseData.priority]],
    [t("detail.metaBy"), caseData.created_by.name],
    [t("detail.metaCreated"), fmtDateTime(caseData.created_at)],
    [t("detail.metaUpdated"), fmtDateTime(caseData.updated_at)],
  ];

  return (
    <div className="space-y-5" data-testid="case-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" data-testid="back-to-cases-button" onClick={() => navigate("/cases")} className="mt-0.5">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-bold text-delta-navy" data-testid="case-number">{caseData.case_number}</span>
              <StatusBadge status={caseData.status} testid="case-status-badge" />
              <PriorityBadge priority={caseData.priority} />
            </div>
            <h1 className="mt-1 font-heading text-xl font-bold text-slate-900 sm:text-2xl" data-testid="case-title">{caseData.title}</h1>
          </div>
        </div>
        {isStaff && (
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="edit-case-button" onClick={openEdit} className="border-slate-300">
              <Pencil className="me-2 h-4 w-4" /> {t("detail.edit")}
            </Button>
            <Button data-testid="change-status-button" onClick={() => { setNewStatus(caseData.status); setStatusOpen(true); }}
              className="bg-delta-navy hover:bg-delta-navyDark">
              {t("detail.changeStatus")}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">{t("detail.details")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700" data-testid="case-description">{caseData.description}</p>
              <div className="grid grid-cols-2 gap-3 border-t pt-4 sm:grid-cols-3">
                {meta.map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">{k}</p>
                    <p className="mt-0.5 text-sm font-medium text-slate-800">{v}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="attachments-card">
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="font-heading text-lg">{t("detail.attachments", { count: attachments.length })}</CardTitle>
              <div>
                <input ref={fileRef} type="file" hidden data-testid="case-file-upload-input"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xlsx,.xls,.txt,.csv" onChange={uploadFile} />
                <Button variant="outline" size="sm" data-testid="upload-attachment-button" disabled={uploadBusy}
                  onClick={() => fileRef.current?.click()} className="border-delta-limeBorder text-delta-navy hover:bg-delta-limeLight">
                  {uploadBusy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Upload className="me-2 h-4 w-4" />}
                  {t("detail.upload")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {attachments.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400" data-testid="attachments-empty">{t("detail.noAttachments")}</p>
              ) : (
                <ul className="divide-y" data-testid="attachments-list">
                  {attachments.map((a) => (
                    <li key={a.id} className="flex items-center gap-3 py-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50">
                        <FileText className="h-4 w-4 text-red-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-800">{a.original_filename}</p>
                        <p className="text-xs text-slate-400">
                          {fmtSize(a.size)} · {a.uploaded_by.name} ({L.role[a.uploaded_by.role]}) · {fmtDateTime(a.created_at)}
                        </p>
                      </div>
                      <Button variant="ghost" size="icon" data-testid={`download-attachment-${a.id}`}
                        onClick={() => downloadAttachment(a)} className="text-delta-navy hover:bg-delta-limeLight">
                        <Download className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          {isStaff && (
            <Card data-testid="assignment-card">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">{t("detail.assignedLawyer")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select
                  value={caseData.assigned_lawyer?.id || "none"}
                  onValueChange={(v) => assignLawyer(v === "none" ? "" : v)}
                >
                  <SelectTrigger data-testid="assign-lawyer-select"><SelectValue placeholder={t("common.unassigned")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("common.unassigned")}</SelectItem>
                    {lawyers.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {user.role === "lawyer" && caseData.assigned_lawyer?.id !== user.id && (
                  <Button variant="outline" size="sm" data-testid="assign-to-me-button" onClick={() => assignLawyer(user.id)} className="w-full">
                    <UserPlus className="me-2 h-4 w-4" /> {t("detail.assignToMe")}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          <Card data-testid="timeline-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <MessageSquare className="h-4 w-4 text-delta-lime" /> {t("detail.timeline")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-h-96 space-y-3 overflow-y-auto pr-1" data-testid="timeline-list">
                {comments.length === 0 && (
                  <p className="py-4 text-center text-sm text-slate-400">{t("detail.noActivity")}</p>
                )}
                {comments.map((c) => (
                  <div key={c.id} className={`rounded-lg border p-3 ${c.kind === "status" ? "border-delta-limeBorder bg-delta-limeLight/40" : "bg-white"}`}
                    data-testid={`timeline-entry-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-700">
                        {c.author.name} <span className="font-normal text-slate-400">· {L.role[c.author.role]}</span>
                      </p>
                      <p className="shrink-0 text-[11px] text-slate-400">{fmtDateTime(c.created_at)}</p>
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{c.message}</p>
                    {c.note && <p className="mt-1 border-l-2 border-delta-lime pl-2 text-xs italic text-slate-500">{c.note}</p>}
                  </div>
                ))}
              </div>
              <form onSubmit={submitComment} className="flex gap-2 border-t pt-3">
                <Input
                  data-testid="comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("detail.addComment")}
                />
                <Button type="submit" size="icon" disabled={commentBusy || !comment.trim()} data-testid="send-comment-button"
                  className="shrink-0 bg-delta-navy hover:bg-delta-navyDark">
                  {commentBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent data-testid="status-change-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("detail.statusDialog")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label>{t("detail.newStatus")}</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-change-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.filter((s) => s !== caseData.status).map((s) => (
                    <SelectItem key={s} value={s}>{L.status[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("detail.note")}</Label>
              <Textarea
                rows={3}
                data-testid="status-comment-input"
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                placeholder={t("detail.notePh")}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" data-testid="status-cancel-button" onClick={() => setStatusOpen(false)}>{t("common.cancel")}</Button>
              <Button data-testid="status-confirm-button" onClick={submitStatus} disabled={statusBusy}
                className="bg-delta-navy hover:bg-delta-navyDark">
                {statusBusy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {t("detail.updateStatus")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-case-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("detail.editCase")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label>{t("newCase.titleLabel")}</Label>
              <Input data-testid="edit-title-input" value={editForm.title || ""}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>{t("newCase.type")}</Label>
                <Select value={editForm.case_type} onValueChange={(v) => setEditForm({ ...editForm, case_type: v })}>
                  <SelectTrigger data-testid="edit-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="complaint">{L.type.complaint}</SelectItem>
                    <SelectItem value="case">{L.type.case}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("newCase.company")}</Label>
                <Select value={editForm.company} onValueChange={(v) => setEditForm({ ...editForm, company: v })}>
                  <SelectTrigger data-testid="edit-company-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="b2b">{L.company.b2b}</SelectItem>
                    <SelectItem value="outpatient">{L.company.outpatient}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("newCase.priority")}</Label>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                  <SelectTrigger data-testid="edit-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(L.priority).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("newCase.desc")}</Label>
              <Textarea rows={5} data-testid="edit-description-input" value={editForm.description || ""}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" data-testid="edit-cancel-button" onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
              <Button data-testid="edit-save-button" onClick={submitEdit} disabled={editBusy}
                className="bg-delta-navy hover:bg-delta-navyDark">
                {editBusy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {t("common.saveChanges")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="hidden">
        <Paperclip />
      </div>
    </div>
  );
}
